import OpenInNewWindow from "@DSG/Icons/OpenInNewWindow";
import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import colorPallete, { bodyFonts, elevation, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    useGetLink, useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    topTitleContent = "topTitleContent",
    fieldsData = "fieldsData",
    backgroundColor = "backgroundColor",
    showAdditionalContentOnBottom = "showAdditionalContentOnBottom",
    additionalContentOnBottom = "additionalContentOnBottom",
}

interface FieldItem {
    fields: {
        image: string;
        imageAltText: string;
        name: string; 
        visitSiteLinkText: string;
        visitSiteLinkUrl: LinkFieldValue;
        openLinkInNewWindow: boolean;
    }
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.topTitleContent]: string;
        [fields.fieldsData]: FieldItem[];
        [fields.backgroundColor]: string;
        [fields.showAdditionalContentOnBottom]: boolean;
        [fields.additionalContentOnBottom]: string;
    };
}

export interface DsgPartnerOrganizationStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    headingWrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    card?: InjectableCss;
    cardTitle?: TypographyProps;
    cardLink?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
}

export const dsgPartnerOrganizationStyles: DsgPartnerOrganizationStyles = {
    wrapper: {
        css: css`
            margin: 24px 0;
            padding: 24px 16px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin: 24px -24px;
                        padding: 48px 24px;
                    `,
                ],
                "min",
            )}
        `
    },
    innerWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    max-width: 1062px;
                    margin: 0 auto;
                `,
                ],"min"
            )}
        `
    },
    headingWrapper: {
        css: css`
            margin-bottom: 24px;
            > div,
            & {
                display: flex;
                gap: 16px; 
                flex-direction: column;
                > [class*="TypographyStyle"] {
                    margin-bottom: 0;
                }
            }
            p {
                color: ${colorPallete.grey800};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin: 0 auto 40px;
                    > div,
                    & {
                        gap: 8px; 
                    }
                `,
                ],"min"
            )}
        `
    },
    cardWrapper: {
        css: css`
            display: flex;
            gap: 40px 8px;
            flex-wrap: wrap;
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    gap: 40px;
                `,
                ],"min"
            )}
        `
    },
    card: {
        css: css`
            display: flex;
            padding: 24px 16px;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 8px;
            width: calc(100%/2 - 4px);
            border-radius: 8px;
            background: ${colorPallete.white};
            box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    width: calc(100%/3 - 30px);
                `,
                css`
                    width: calc(100%/4 - 30px);
                `,
                ],"min"
            )}
        `
    },
    imageWrapper: {
        css: css`
            margin-bottom: auto;
            display: flex;
            height: 100%;
            align-items: center;
            img {
                width: auto;
                height: auto;
                max-width: 100%;
                align-items: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    height: 100px;
                    margin-bottom: 0;
                    min-height: 100px;
                    max-height: 100px;
                    img {
                        max-height: 100px;
                    }
                `,
                ],"min"
            )}
        `  
    },
    cardTitle: {
        variant: "p",
        size: bodyFonts.body2SemiBoldLg.fontSize,
        lineHeight: bodyFonts.body2SemiBoldLg.lineHeight,
        weight: bodyFonts.body2SemiBoldLg.fontWeight,
        color: colorPallete.grey800,
        css: css`
            text-align: center;
            margin: 0;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    min-height: 42px;
                `,
                ],"min"
            )}
        `
    },
    cardLink: {
        css: css`
            span {
                font: ${bodyFonts.body2SemiBoldLg.fontWeight} ${bodyFonts.body2SemiBoldLg.fontSize}/${bodyFonts.body2SemiBoldLg.lineHeight} ${bodyFonts.body2SemiBoldLg.fontFamily};
                display: flex;
                align-items: center;
                gap: 4px;
            }
            svg {
                width: 20px;
                height: 20px;
            }
            &.withoutNewWindowIcon {
                margin-bottom: 2px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin-top: auto;
                `,
                ],"min"
            )}
        `
    },
    bottomContentWrapper: {
        css: css`
            margin-top: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin-top: 40px;
                `,
                ],"min"
            )}
        `
    }
};

const styles = dsgPartnerOrganizationStyles;

export const DsgPartnerOrganization: FC<OwnProps> = ({ fields }) => {

    const { fieldsData } = fields;
    const linkUrls = useGetLinks(fields.fieldsData, o => o.fields.visitSiteLinkUrl);

    return (
        <StyledWrapper {...styles.wrapper}
            className="organizationWrapper"  
            style={{backgroundColor: fields.backgroundColor ? `${fields.backgroundColor}` : '#F6F6F7'}}>
            <StyledWrapper {...styles.innerWrapper}>
                {fields.topTitleContent && (
                    <StyledWrapper {...styles.headingWrapper}>
                        {parse(fields.topTitleContent, parserOptions)}
                    </StyledWrapper>
                )}
                {fieldsData.length > 0 && (
                    <StyledWrapper {...styles.cardWrapper}>
                        {fieldsData.map((item, index) => {
                            const linkUrl = linkUrls[index];
                            return (
                                <StyledWrapper {...styles.card} key={index}>
                                    {item.fields.image && (
                                        <StyledWrapper {...styles.imageWrapper}>
                                            <Img src={item.fields.image} altText={item.fields.imageAltText || ''} />
                                        </StyledWrapper>
                                    )}
                    
                                    {item.fields.name && (
                                        <Typography {...styles.cardTitle}>{item.fields.name}</Typography>
                                    )}
                                    {(item.fields.visitSiteLinkText && item.fields.visitSiteLinkUrl)  && (
                                        <Link href={linkUrl.url} 
                                            target={item.fields.openLinkInNewWindow ? '_blank': ''} 
                                            {...styles.cardLink}   
                                            className={`text-link text-link-primary text-link-active ${item.fields.openLinkInNewWindow ? '' : 'withoutNewWindowIcon'}`}
                                        >
                                            {item.fields.visitSiteLinkText} {item.fields.openLinkInNewWindow && (
                                                <OpenInNewWindow/>
                                            )}
                                        </Link>
                                    )}
                                
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                )}
                {fields.additionalContentOnBottom && (
                    <StyledWrapper {...styles.bottomContentWrapper}>
                        {parse(fields.additionalContentOnBottom, parserOptions)}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};



const widgetModule: WidgetModule = {
    component: DsgPartnerOrganization,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.topTitleContent,
                editorTemplate: "RichTextField",
                displayName: "Top Title Section",
                defaultValue: "",
                fieldType: "General",
                extendedConfig: { height: 170 },
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "#F6F6F7",
            },
            {
                name: fields.fieldsData,
                editorTemplate: "ListField",
                displayName: "Fields Data",
                getDisplay: (item: HasFields) => {
                    const {
                        name,
                    } = item.fields;
                    if (name) {
                        return name;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "image",
                        displayName: 'Image',
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "imageAltText",
                        editorTemplate: "TextField",
                        displayName: "Image Alt Text",
                        defaultValue: "",
                    },
                    {
                        name: "visitSiteLinkText",
                        displayName: "Visit Site Link Text",
                        editorTemplate: "TextField",
                        defaultValue: "Visit site",
                    },
                    {
                        name: "visitSiteLinkUrl",
                        displayName: "Visit Site Link Url",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "openLinkInNewWindow",
                        displayName: "Open Link In New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                ],
            },
            {
                name: fields.showAdditionalContentOnBottom,
                displayName: "Show additional content on bottom",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.additionalContentOnBottom,
                editorTemplate: "RichTextField",
                displayName: "Additional Content On Bottom",
                defaultValue: "",
                extendedConfig: { height: 170 },
                fieldType: "General",
                isVisible: widget => widget.fields.showAdditionalContentOnBottom,
            },
        ],
    },
};

export default widgetModule;
